import { useState, useEffect, useCallback, useRef } from 'react'

// Event types from the intelligent execution system
export interface IntelligentExecutionEvent {
  type: string
  execution_id: string
  phase: string
  timestamp: string
  [key: string]: any
}

export interface ExecutionStep {
  id: string
  type: string
  phase: string
  timestamp: string
  title: string
  description: string
  status: 'pending' | 'running' | 'completed' | 'error' | 'revision'
  specialist?: string
  taskId?: string
  qualityScore?: number
  duration?: number
  isParallel?: boolean
  parallelGroup?: number
  metadata?: Record<string, any>
}

export interface TaskState {
  id: string
  description: string
  specialist: string
  status: 'pending' | 'running' | 'completed' | 'reviewing' | 'revising'
  qualityScore?: number
  revisionCount: number
  isParallel: boolean
  parallelGroup?: number
}

interface UseIntelligentExecutionWSOptions {
  userId: string
  token: string
  onEvent?: (event: IntelligentExecutionEvent) => void
}

export function useIntelligentExecutionWS(options: UseIntelligentExecutionWSOptions) {
  const { userId, token, onEvent } = options
  const wsRef = useRef<WebSocket | null>(null)
  const onEventRef = useRef(onEvent)
  const [isConnected, setIsConnected] = useState(false)
  const [currentExecutionId, setCurrentExecutionId] = useState<string | null>(null)
  const [steps, setSteps] = useState<ExecutionStep[]>([])
  const [tasks, setTasks] = useState<TaskState[]>([])
  const [currentPhase, setCurrentPhase] = useState<string>('idle')
  const [error, setError] = useState<string | null>(null)

  // Keep onEvent ref current without triggering reconnections
  useEffect(() => { onEventRef.current = onEvent }, [onEvent])

  // Connect to WebSocket
  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return
    if (!token) return

    const wsUrl = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/api/v1/ws?token=${token}`
    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      // Send auth message
      ws.send(JSON.stringify({ type: 'auth', user_id: userId }))
    }

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)

        if (data.type === 'connected') {
          setIsConnected(true)
          setError(null)
        } else if (data.type === 'subscribed') {
          console.log(`Subscribed to channel: ${data.channel}`)
        } else if (data.type?.startsWith('intelligent.')) {
          handleIntelligentEvent(data)
          onEventRef.current?.(data)
        }
      } catch (err) {
        console.error('Failed to parse WebSocket message:', err)
      }
    }

    ws.onerror = () => {
      setError('WebSocket connection error')
      setIsConnected(false)
    }

    ws.onclose = () => {
      setIsConnected(false)
    }

    wsRef.current = ws
  }, [userId, token])

  // Subscribe to execution channel
  const subscribeToExecution = useCallback((executionId: string) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'subscribe',
        channel: `intelligent_execution:${executionId}`
      }))
      setCurrentExecutionId(executionId)
    }
  }, [])

  // Handle intelligent execution events
  const handleIntelligentEvent = useCallback((event: IntelligentExecutionEvent) => {
    const step = eventToStep(event)
    if (step) {
      setSteps(prev => [...prev, step])
    }

    setCurrentPhase(event.phase)

    switch (event.type) {
      case 'intelligent.execution_start':
        setSteps([])
        setTasks([])
        setCurrentPhase('starting')
        break

      case 'intelligent.supervisor_analysis':
        // Initialize tasks from analysis
        if (event.tasks) {
          setTasks(event.tasks.map((t: any) => ({
            id: t.id,
            description: t.description,
            specialist: t.specialist_type,
            status: 'pending',
            revisionCount: 0,
            isParallel: false,
            parallelGroup: undefined
          })))
        }
        break

      case 'intelligent.specialist_start':
        setTasks(prev => prev.map(t =>
          t.id === event.task_id
            ? { ...t, status: 'running', isParallel: event.is_parallel, parallelGroup: event.parallel_group }
            : t
        ))
        break

      case 'intelligent.specialist_complete':
        setTasks(prev => prev.map(t =>
          t.id === event.task_id
            ? { ...t, status: 'reviewing' }
            : t
        ))
        break

      case 'intelligent.quality_review_result':
        setTasks(prev => prev.map(t =>
          t.id === event.task_id
            ? {
                ...t,
                status: event.approved ? 'completed' : 'revising',
                qualityScore: event.quality_score,
                revisionCount: event.revision_count
              }
            : t
        ))
        break

      case 'intelligent.revision_start':
        setTasks(prev => prev.map(t =>
          t.id === event.task_id
            ? { ...t, status: 'running' }
            : t
        ))
        break

      case 'intelligent.execution_complete':
        setCurrentPhase('completed')
        break

      case 'intelligent.execution_error':
        setError(event.error)
        setCurrentPhase('error')
        break
    }
  }, [])

  // Reset state
  const reset = useCallback(() => {
    setSteps([])
    setTasks([])
    setCurrentPhase('idle')
    setError(null)
    setCurrentExecutionId(null)
  }, [])

  // Disconnect
  const disconnect = useCallback(() => {
    wsRef.current?.close()
    wsRef.current = null
    setIsConnected(false)
  }, [])

  useEffect(() => {
    return () => {
      disconnect()
    }
  }, [disconnect])

  return {
    isConnected,
    connect,
    disconnect,
    subscribeToExecution,
    currentExecutionId,
    steps,
    tasks,
    currentPhase,
    error,
    reset
  }
}

// Convert event to display step
function eventToStep(event: IntelligentExecutionEvent): ExecutionStep | null {
  const baseStep = {
    id: `${event.type}-${event.timestamp}`,
    type: event.type,
    phase: event.phase,
    timestamp: event.timestamp,
    metadata: event
  }

  switch (event.type) {
    case 'intelligent.execution_start':
      return {
        ...baseStep,
        title: 'Execution Started',
        description: `Processing: "${event.original_prompt?.slice(0, 100)}..."`,
        status: 'running'
      }

    case 'intelligent.supervisor_analysis':
      return {
        ...baseStep,
        title: 'Supervisor Analysis Complete',
        description: `${event.task_count} tasks identified, complexity: ${event.total_complexity?.toFixed(1)}`,
        status: 'completed'
      }

    case 'intelligent.specialist_start':
      return {
        ...baseStep,
        title: `${formatSpecialist(event.specialist_type)} Started`,
        description: event.task_description,
        status: 'running',
        specialist: event.specialist_type,
        taskId: event.task_id,
        isParallel: event.is_parallel,
        parallelGroup: event.parallel_group
      }

    case 'intelligent.specialist_complete':
      return {
        ...baseStep,
        title: `${formatSpecialist(event.specialist_type)} Complete`,
        description: event.result_preview?.slice(0, 150) || 'Task completed',
        status: 'completed',
        specialist: event.specialist_type,
        taskId: event.task_id,
        duration: event.duration_ms
      }

    case 'intelligent.quality_review_start':
      return {
        ...baseStep,
        title: 'Quality Review',
        description: `Reviewing ${formatSpecialist(event.specialist_type)} output`,
        status: 'running',
        specialist: event.specialist_type,
        taskId: event.task_id
      }

    case 'intelligent.quality_review_result':
      return {
        ...baseStep,
        title: event.approved ? 'Quality Approved' : 'Revision Needed',
        description: event.feedback || `Score: ${event.quality_score}/10`,
        status: event.approved ? 'completed' : 'revision',
        qualityScore: event.quality_score,
        taskId: event.task_id
      }

    case 'intelligent.revision_start':
      return {
        ...baseStep,
        title: `Revision #${event.revision_number}`,
        description: event.feedback,
        status: 'running',
        specialist: event.specialist_type,
        taskId: event.task_id
      }

    case 'intelligent.parallel_group_start':
      return {
        ...baseStep,
        title: `Parallel Group ${event.group_id} Started`,
        description: `${event.task_ids?.length} tasks running in parallel: ${event.specialists?.join(', ')}`,
        status: 'running',
        isParallel: true,
        parallelGroup: event.group_id
      }

    case 'intelligent.parallel_group_complete':
      return {
        ...baseStep,
        title: `Parallel Group ${event.group_id} Complete`,
        description: 'All parallel tasks finished',
        status: 'completed',
        isParallel: true,
        parallelGroup: event.group_id
      }

    case 'intelligent.aggregation_start':
      return {
        ...baseStep,
        title: 'Final Aggregation',
        description: `Combining results from ${event.task_count} tasks`,
        status: 'running'
      }

    case 'intelligent.execution_complete':
      return {
        ...baseStep,
        title: 'Execution Complete',
        description: `Quality: ${event.quality_score}/10, Tasks: ${event.total_tasks}, Cost: $${event.total_cost?.toFixed(4)}`,
        status: 'completed',
        qualityScore: event.quality_score,
        duration: event.duration_ms
      }

    case 'intelligent.execution_error':
      return {
        ...baseStep,
        title: 'Execution Error',
        description: event.error,
        status: 'error'
      }

    default:
      return null
  }
}

function formatSpecialist(type: string): string {
  return type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export default useIntelligentExecutionWS
